/**
 * Study import operations — format detection, raw import, student inference.
 *
 * Legacy format conversion lives in study-import-legacy.ts.
 */

import type { Study, StudyGroup, Student } from '@skjv/common';
import { decodeSkjv } from '@/services/skjv-codec';
import { importLegacyStudy } from './study-import-legacy';
import { migrateGroupLevels, migrateStudyLevels } from './level-migration';

/** Result of an import operation */
export type ImportResult =
  | {
      studiesCount: number;
      groupsCount: number;
      /** Items skipped because their id already exists locally. */
      duplicateStudies?: number;
      duplicateGroups?: number;
      needsGroup?: never;
    }
  | { needsGroup: true; pendingData: Record<string, unknown>; students: Student[] };

/** Helpers that the import functions need from the parent StudyState */
export interface StudyStoreDeps {
  studies: Study[];
  studyGroups: StudyGroup[];
  generateId: () => string;
  now: () => string;
  saveStudies: () => void;
  saveStudyGroups: () => void;
}

/** Extract unique students from raw import data (assignments or embedded arrays) */
export function inferStudentsFromData(data: Record<string, unknown>): Student[] {
  const assignments = data.assignments as Array<{ student?: Student }> | undefined;
  if (!assignments?.length) return [];

  const seen = new Set<string>();
  return assignments
    .filter((a) => a.student && !seen.has(a.student.id) && seen.add(a.student.id))
    .map((a) => a.student!);
}

/** Import studies - supports hex-encoded .skjv format and legacy plain JSON */
export function importFromJson(rawData: string, deps: StudyStoreDeps): ImportResult {
  try {
    const jsonData = decodeSkjv(rawData);
    const data = JSON.parse(jsonData);
    let studiesCount = 0;
    let groupsCount = 0;

    // Legacy format (single study with embedded studyGroup)
    if (data.name && data.startingLocation && data.studyGroup) {
      const result = importLegacyStudy(data, deps);
      return {
        studiesCount: result.isStudyImported ? 1 : 0,
        groupsCount: result.isGroupImported ? 1 : 0,
        duplicateStudies: result.isStudyImported ? 0 : 1,
      };
    }

    // Raw single study format — needs group name from user
    if (data.name && data.startingLocation && !data.studyGroup && !data.studies) {
      const students = inferStudentsFromData(data);
      return { needsGroup: true, pendingData: data, students };
    }

    // New format: { studies: [...], studyGroups: [...] }
    let duplicateStudies = 0;
    let duplicateGroups = 0;
    if (Array.isArray(data.studyGroups)) {
      const existingIds = new Set(deps.studyGroups.map((g) => g.id));
      for (const group of migrateGroupLevels(data.studyGroups)) {
        if (!existingIds.has(group.id)) {
          deps.studyGroups.push(group);
          groupsCount++;
        } else {
          duplicateGroups++;
        }
      }
      deps.saveStudyGroups();
    }

    if (Array.isArray(data.studies)) {
      const existingIds = new Set(deps.studies.map((s) => s.id));
      for (const study of migrateStudyLevels(data.studies)) {
        if (!existingIds.has(study.id)) {
          deps.studies.push(study);
          studiesCount++;
        } else {
          duplicateStudies++;
        }
      }
      deps.saveStudies();
    }

    return { studiesCount, groupsCount, duplicateStudies, duplicateGroups };
  } catch {
    return { studiesCount: 0, groupsCount: 0 };
  }
}

/** Complete import of a raw study by creating a new group with the given name */
export function importRawStudyWithGroup(
  data: Record<string, unknown>,
  groupName: string,
  deps: StudyStoreDeps,
): { studiesCount: number; groupsCount: number } {
  const students = inferStudentsFromData(data);
  const groupId = deps.generateId();
  const now = deps.now();

  const [group] = migrateGroupLevels([
    {
      id: groupId,
      name: groupName,
      leaderCount: 1,
      teachers: [],
      students,
      lastUsedAt: now,
      updatedAt: now,
    },
  ]);
  deps.studyGroups.push(group);
  deps.saveStudyGroups();

  const [study] = migrateStudyLevels([{
    id: deps.generateId(),
    name: data.name as string,
    createdAt: (data.createdAt as string) || now,
    updatedAt: now,
    startingLocation: data.startingLocation as Study['startingLocation'],
    endingLocation: data.endingLocation as Study['endingLocation'],
    studyGroupId: groupId,
    scenes: (data.scenes as Study['scenes']) || [],
    writingLocation: (data.writingLocation || data.startingLocation) as Study['writingLocation'],
    memoryStartingLocation: data.memoryStartingLocation as Study['memoryStartingLocation'],
    memoryEndingLocation: data.memoryEndingLocation as Study['memoryEndingLocation'],
    assignments: (data.assignments as Study['assignments']) || [],
    teacherAssignments: (data.teacherAssignments as Study['teacherAssignments']) || [],
  }]);
  deps.studies.push(study);
  deps.saveStudies();

  return { studiesCount: 1, groupsCount: 1 };
}
